import React, { useEffect, useMemo, useState } from "react";
import { Check, Loader2, Pencil, Plus, Search, Tag, Trash2, X } from "lucide-react";
import { toast } from "react-toastify";

import axiosInstance from "../utils/axiosInstance";

const ControlledKeywords = () => {
    const [items, setItems] = useState([]);
    const [newKeyword, setNewKeyword] = useState("");
    const [search, setSearch] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editingKeyword, setEditingKeyword] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const loadItems = async () => {
        setLoading(true);
        try {
            const { data } = await axiosInstance.get("/setting/controlled-keywords");
            setItems(Array.isArray(data) ? data : []);
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to load controlled keywords.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadItems();
    }, []);

    const filteredItems = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return items;
        return items.filter((item) => String(item.keyword || "").toLowerCase().includes(term));
    }, [items, search]);

    const createKeyword = async (event) => {
        event.preventDefault();
        const keyword = newKeyword.trim();
        if (!keyword) return;

        setSaving(true);
        try {
            const { data } = await axiosInstance.post("/setting/controlled-keywords", { keyword });
            setItems((current) => [...current, data].sort((a, b) => a.keyword.localeCompare(b.keyword)));
            setNewKeyword("");
            toast.success(`Added "${data.keyword}".`);
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to add controlled keyword.");
        } finally {
            setSaving(false);
        }
    };

    const startEdit = (item) => {
        setEditingId(item.id);
        setEditingKeyword(item.keyword);
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditingKeyword("");
    };

    const updateKeyword = async (item) => {
        const keyword = editingKeyword.trim();
        if (!keyword) return;

        setSaving(true);
        try {
            const { data } = await axiosInstance.put(
                `/setting/controlled-keywords/${encodeURIComponent(item.id)}`,
                { keyword }
            );
            setItems((current) =>
                current
                    .map((entry) => (entry.id === item.id ? data : entry))
                    .sort((a, b) => a.keyword.localeCompare(b.keyword))
            );
            cancelEdit();
            toast.success("Controlled keyword updated.");
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to update controlled keyword.");
        } finally {
            setSaving(false);
        }
    };

    const deleteKeyword = async (item) => {
        if (!window.confirm(`Remove "${item.keyword}" from the controlled list?`)) return;

        setSaving(true);
        try {
            await axiosInstance.delete(`/setting/controlled-keywords/${encodeURIComponent(item.id)}`);
            setItems((current) => current.filter((entry) => entry.id !== item.id));
            if (editingId === item.id) cancelEdit();
            toast.success("Controlled keyword removed.");
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to remove controlled keyword.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 px-3 py-6 sm:px-6">
            <div className="mx-auto w-full max-w-5xl">
                <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
                    <div className="border-b border-slate-200 bg-[radial-gradient(circle_at_top_right,_#dbeafe,_transparent_42%),linear-gradient(135deg,#f8fafc,#ffffff)] px-5 py-7 sm:px-8">
                        <div className="flex items-start gap-4">
                            <div className="rounded-2xl bg-slate-900 p-3 text-white shadow-sm">
                                <Tag className="h-6 w-6" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-extrabold tracking-tight text-slate-950 sm:text-3xl">
                                    Controlled Keywords
                                </h1>
                                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
                                    If a website includes any of these specific words, those words are automatically added to its AI keywords.
                                </p>
                            </div>
                        </div>

                        <form onSubmit={createKeyword} className="mt-6 flex flex-col gap-2 sm:flex-row">
                            <label htmlFor="new-controlled-keyword" className="sr-only">
                                New controlled keyword
                            </label>
                            <input
                                id="new-controlled-keyword"
                                value={newKeyword}
                                onChange={(event) => setNewKeyword(event.target.value)}
                                maxLength={100}
                                placeholder="Add a brand or term, e.g. Alspec"
                                className="h-12 flex-1 rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-950 shadow-sm outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-900/15"
                            />
                            <button
                                type="submit"
                                disabled={saving || !newKeyword.trim()}
                                className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-slate-900 px-5 text-sm font-bold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
                            >
                                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                                Add keyword
                            </button>
                        </form>
                    </div>

                    <div className="p-5 sm:p-8">
                        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="text-sm font-semibold text-slate-700">
                                {items.length} controlled keyword{items.length === 1 ? "" : "s"}
                            </div>
                            <div className="relative w-full sm:w-72">
                                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                <input
                                    value={search}
                                    onChange={(event) => setSearch(event.target.value)}
                                    placeholder="Search keywords"
                                    className="h-10 w-full rounded-xl border border-slate-300 bg-white pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-slate-900/15"
                                />
                            </div>
                        </div>

                        <div className="overflow-hidden rounded-2xl border border-slate-200">
                            {loading ? (
                                <div className="flex items-center justify-center gap-2 px-4 py-14 text-sm font-semibold text-slate-600">
                                    <Loader2 className="h-5 w-5 animate-spin" />
                                    Loading keywords...
                                </div>
                            ) : filteredItems.length ? (
                                <ul className="divide-y divide-slate-200">
                                    {filteredItems.map((item) => (
                                        <li key={item.id} className="flex min-h-16 items-center gap-3 px-4 py-3 sm:px-5">
                                            {editingId === item.id ? (
                                                <input
                                                    autoFocus
                                                    value={editingKeyword}
                                                    onChange={(event) => setEditingKeyword(event.target.value)}
                                                    onKeyDown={(event) => {
                                                        if (event.key === "Enter") updateKeyword(item);
                                                        if (event.key === "Escape") cancelEdit();
                                                    }}
                                                    maxLength={100}
                                                    className="h-10 min-w-0 flex-1 rounded-xl border border-slate-300 px-3 text-sm outline-none focus:ring-2 focus:ring-slate-900/15"
                                                />
                                            ) : (
                                                <div className="min-w-0 flex-1 break-words text-sm font-bold text-slate-900">
                                                    {item.keyword}
                                                </div>
                                            )}

                                            <div className="flex shrink-0 items-center gap-1">
                                                {editingId === item.id ? (
                                                    <>
                                                        <button type="button" onClick={() => updateKeyword(item)} disabled={saving} aria-label={`Save ${item.keyword}`} className="rounded-lg p-2 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40">
                                                            <Check className="h-4 w-4" />
                                                        </button>
                                                        <button type="button" onClick={cancelEdit} aria-label="Cancel editing" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100">
                                                            <X className="h-4 w-4" />
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button type="button" onClick={() => startEdit(item)} aria-label={`Edit ${item.keyword}`} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900">
                                                            <Pencil className="h-4 w-4" />
                                                        </button>
                                                        <button type="button" onClick={() => deleteKeyword(item)} disabled={saving} aria-label={`Delete ${item.keyword}`} className="rounded-lg p-2 text-rose-600 hover:bg-rose-50 disabled:opacity-40">
                                                            <Trash2 className="h-4 w-4" />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div className="px-5 py-14 text-center">
                                    <Tag className="mx-auto h-7 w-7 text-slate-300" />
                                    <p className="mt-3 text-sm font-bold text-slate-700">
                                        {search ? "No matching keywords" : "No controlled keywords yet"}
                                    </p>
                                    <p className="mt-1 text-xs text-slate-500">
                                        {search ? "Try a different search." : "Add Alspec, AWS, Capral, or another term above."}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default ControlledKeywords;
